import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';

import { Auth0ContextType, FuLLAuth0UserProfile } from 'types/auth';
import { Auth0Service } from '../services';
import Loader from '../components/Loader';
import { config } from '../env';
import { analytics } from 'lib/analytics';
const AUTH0_API = config.AUTH0_API;

// constant
const service = new Auth0Service();

const initialState: Auth0ContextType = {
  isInitialized: false,
  getAccessToken: () => {
    return Promise.resolve('');
  },
  refreshToken: () => {
    return Promise.resolve('');
  },
  login: service.login,
  signUp: service.signUp,
  logout: service.logout,
  accessToken: '',
  isLoading: true,
  setUser: (user) => {
    return {};
  },
  resetPassword: (email: string) => {
    return service.changePassword({ connection: AUTH0_API.connection, email });
  },
  forgotPassword: (email: string) => {
    return service.forgotPassword({ connection: AUTH0_API.connection, email });
  }
};

/*const initialAuth: AuthProps = {
  isLoggedIn: false,
  isInitialized: false,
  user: null,
  token: null
};*/

// ==============================|| AUTH0 CONTEXT & PROVIDER ||============================== //

const Auth0Context = createContext<Auth0ContextType | null>(null);

export const Auth0Provider = ({ children }: { children: React.ReactElement }) => {
  // const [state, dispatch] = useReducer(authReducer, initialAuth);
  const [currentState, setCurrentState] = useState<Auth0ContextType>(initialState);
  const [user, setUser] = useState<FuLLAuth0UserProfile | undefined>();

  // Identify with the Auth0 subject so PostHog feature-flag bucketing is
  // deterministic per user across devices. Re-identifying with the same id
  // is a no-op in posthog-js.
  useEffect(() => {
    if (user?.sub) {
      analytics.identifyUser(user.sub, { email: user.email });
    }
  }, [user?.sub, user?.email]);

  // Stable for the provider's lifetime: every use*Client hook memoises on
  // [accessToken, refreshToken], so a new function per render would hand each
  // consumer a new client callback and re-run every effect that depends on it.
  // Functional updates keep it from spreading a stale `currentState`.
  const refreshToken = useCallback(() => {
    return new Promise((resolve, reject) => {
      service
        .checkSession()
        .then((token) => {
          setCurrentState((prev) => ({
            ...prev,
            isLoading: false,
            error: null,
            isAuthenticated: true,
            accessToken: token
          }));
          const userInfo = localStorage.getItem('userInfo');
          let userP;
          if (userInfo) {
            userP = JSON.parse(userInfo);
          }
          if (userP && userP?.rateLimitRemaining <= 1 && userP?.rateLimitResetTime * 1000 > new Date().getTime()) {
            localStorage.setItem('userInfo', JSON.stringify({ ...userP }));
            let user: FuLLAuth0UserProfile = userP;
            let metadata_key = AUTH0_API.namespace + '/user_metadata';
            let app_metadata_key = AUTH0_API.namespace + '/app_metadata';
            let key = AUTH0_API.namespace + '/firstName';

            user.firstName = (userP as any)[key] || userP.name;
            user.name =
              !!(userP as any)[metadata_key]?.firstName && !!(userP as any)[metadata_key]?.lastName
                ? (userP as any)[metadata_key]?.firstName + ' ' + (userP as any)[metadata_key]?.lastName
                : userP.name;
            user.tcId = (userP as any)[app_metadata_key]?.internal_user_id;
            user.accountLimit = (userP as any)[app_metadata_key]?.account_limit;
            user.futuresAccountLimit = (userP as any)[app_metadata_key]?.futures_account_limit;
            user.currency = (userP as any)[metadata_key]?.currency;
            user.timezone = (userP as any)[metadata_key]?.timezone;
            user.roles = (userP as any)[AUTH0_API.namespace + '/roles'] || [];
            user.tourCompleted = (userP as any)[metadata_key]?.tourCompleted === 'TRUE';
            user.isBetaUser = !!user?.roles?.find((e) => e === 'BETA');
            user.isAdminUser = !!user?.roles?.find((e) => e === 'Admin');
            user.isDediEnvUser = !!user?.roles?.find((e) => e === 'Dedicated Env');
            user.isOverDueUser = !!user?.roles?.find((e) => e === 'Overdue Subscription');
            setUser(user);
            const isSignUpFinished =
              !!(userP as any)[metadata_key]?.onboardingComplete && (userP as any)[metadata_key]?.onboardingComplete === 'TRUE';
            setCurrentState((prev) => ({
              ...prev,
              isLoading: false,
              isInitialized: true,
              error: null,
              isAuthenticated: true,
              accessToken: token,
              isSignUpFinished: isSignUpFinished,
              refreshToken: refreshToken,
              user: user
            }));
            resolve(token);
          } else {
            service
              .getUserInfo(token)
              .then(async (userP) => {
                localStorage.setItem('userInfo', JSON.stringify({ ...userP }));
                let user: FuLLAuth0UserProfile = userP;
                const metadata_key = AUTH0_API.namespace + '/user_metadata';
                const app_metadata_key = AUTH0_API.namespace + '/app_metadata';
                let key = AUTH0_API.namespace + '/firstName';

                user.firstName = (userP as any)[key] || userP.name;
                user.name =
                  !!(userP as any)[metadata_key]?.firstName && !!(userP as any)[metadata_key]?.lastName
                    ? (userP as any)[metadata_key]?.firstName + ' ' + (userP as any)[metadata_key]?.lastName
                    : userP.name;
                user.tcId = (userP as any)[app_metadata_key]?.internal_user_id;
                user.accountLimit = (userP as any)[app_metadata_key]?.account_limit;
                user.futuresAccountLimit = (userP as any)[app_metadata_key]?.futures_account_limit;
                user.currency = (userP as any)[metadata_key]?.currency;
                user.timezone = (userP as any)[metadata_key]?.timezone;
                user.roles = (userP as any)[AUTH0_API.namespace + '/roles'] || [];
                user.tourCompleted = (userP as any)[metadata_key]?.tourCompleted === 'TRUE';
                user.isBetaUser = !!user?.roles?.find((e) => e === 'BETA');
                user.isAdminUser = !!user?.roles?.find((e) => e === 'Admin');
                user.isDediEnvUser = !!user?.roles?.find((e) => e === 'Dedicated Env');
                user.isOverDueUser = !!user?.roles?.find((e) => e === 'Overdue Subscription');
                const isSignUpFinished =
                  !!(userP as any)[metadata_key]?.onboardingComplete && (userP as any)[metadata_key]?.onboardingComplete === 'TRUE';
                setUser(user);
                setCurrentState((prev) => ({
                  ...prev,
                  isLoading: false,
                  isInitialized: true,
                  error: null,
                  isAuthenticated: true,
                  accessToken: token,
                  isSignUpFinished: isSignUpFinished,
                  refreshToken: refreshToken,
                  user: userP
                }));
                resolve(token);
              })
              .catch((er) => {
                reject(er);
              });
          }
        })
        .catch((err) => {
          reject(err);
        });
    });
  }, []);

  useEffect(() => {
    const init = () => {
      // setCurrentState({ ...currentState, isLoading: true, error: null, isAuthenticated: false, isInitialized: true });
      service
        .checkSession()
        .then((token) => {
          setCurrentState({
            ...currentState,
            isLoading: false,
            isInitialized: true,
            error: null,
            isAuthenticated: true,
            accessToken: token
          });
          const userInfo = localStorage.getItem('userInfo');
          let userP;
          if (userInfo) {
            userP = JSON.parse(userInfo);
          }
          if (userP && userP?.rateLimitRemaining <= 1 && userP?.rateLimitResetTime * 1000 > new Date().getTime()) {
            let user: FuLLAuth0UserProfile = userP;
            let metadata_key = AUTH0_API.namespace + '/user_metadata';
            let app_metadata_key = AUTH0_API.namespace + '/app_metadata';
            let key = AUTH0_API.namespace + '/firstName';

            user.firstName = (userP as any)[key] || userP.name;
            user.name =
              !!(userP as any)[metadata_key]?.firstName && !!(userP as any)[metadata_key]?.lastName
                ? (userP as any)[metadata_key]?.firstName + ' ' + (userP as any)[metadata_key]?.lastName
                : userP.name;
            user.tcId = (userP as any)[app_metadata_key]?.internal_user_id;
            user.accountLimit = (userP as any)[app_metadata_key]?.account_limit;
            user.futuresAccountLimit = (userP as any)[app_metadata_key]?.futures_account_limit;
            user.currency = (userP as any)[metadata_key]?.currency;
            user.timezone = (userP as any)[metadata_key]?.timezone;
            user.roles = (userP as any)[AUTH0_API.namespace + '/roles'] || [];
            user.tourCompleted = (userP as any)[metadata_key]?.tourCompleted === 'TRUE';
            user.isBetaUser = !!user?.roles?.find((e) => e === 'BETA');
            user.isAdminUser = !!user?.roles?.find((e) => e === 'Admin');
            user.isDediEnvUser = !!user?.roles?.find((e) => e === 'Dedicated Env');
            user.isOverDueUser = !!user?.roles?.find((e) => e === 'Overdue Subscription');
            setUser(user);
            const isSignUpFinished =
              !!(userP as any)[metadata_key]?.onboardingComplete && (userP as any)[metadata_key]?.onboardingComplete === 'TRUE';
            setCurrentState({
              ...currentState,
              isLoading: false,
              isInitialized: true,
              error: null,
              isAuthenticated: true,
              accessToken: token,
              isSignUpFinished: isSignUpFinished,
              refreshToken: refreshToken,
              user: user
            });
          } else {
            service
              .getUserInfo(token)
              .then(async (userP: any) => {
                localStorage.setItem('userInfo', JSON.stringify({ ...userP }));
                let user: FuLLAuth0UserProfile = userP;
                let metadata_key = AUTH0_API.namespace + '/user_metadata';
                let app_metadata_key = AUTH0_API.namespace + '/app_metadata';
                let key = AUTH0_API.namespace + '/firstName';

                user.firstName = (userP as any)[key] || userP.name;
                user.name =
                  !!(userP as any)[metadata_key]?.firstName && !!(userP as any)[metadata_key]?.lastName
                    ? (userP as any)[metadata_key]?.firstName + ' ' + (userP as any)[metadata_key]?.lastName
                    : userP.name;
                user.tcId = (userP as any)[app_metadata_key]?.internal_user_id;
                user.accountLimit = (userP as any)[app_metadata_key]?.account_limit;
                user.futuresAccountLimit = (userP as any)[app_metadata_key]?.futures_account_limit;
                user.currency = (userP as any)[metadata_key]?.currency;
                user.timezone = (userP as any)[metadata_key]?.timezone;
                user.roles = (userP as any)[AUTH0_API.namespace + '/roles'] || [];
                user.tourCompleted = (userP as any)[metadata_key]?.tourCompleted === 'TRUE';
                user.isBetaUser = !!user?.roles?.find((e) => e === 'BETA');
                user.isAdminUser = !!user?.roles?.find((e) => e === 'Admin');
                user.isDediEnvUser = !!user?.roles?.find((e) => e === 'Dedicated Env');
                user.isOverDueUser = !!user?.roles?.find((e) => e === 'Overdue Subscription');
                setUser(user);
                const isSignUpFinished =
                  !!(userP as any)[metadata_key]?.onboardingComplete && (userP as any)[metadata_key]?.onboardingComplete === 'TRUE';
                setCurrentState({
                  ...currentState,
                  isLoading: false,
                  isInitialized: true,
                  error: null,
                  isAuthenticated: true,
                  accessToken: token,
                  isSignUpFinished: isSignUpFinished,
                  refreshToken: refreshToken,
                  user: user
                });
              })
              .catch((er) => {});
          }
        })
        .catch((err) => {
          setCurrentState({ ...currentState, isLoading: false, isInitialized: true, error: err, isAuthenticated: false });
        });
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*useEffect(() => {
      const init = async () => {
        try {
          await auth0Client.checkSession();
          const isLoggedIn = await auth0Client.isAuthenticated();

          if (isLoggedIn) {
            const user = await auth0Client.getUser();

            dispatch({
              type: LOGIN,
              payload: {
                isLoggedIn: true,
                user: {
                  id: user?.sub,
                  email: user?.email
                }
              }
            });
          } else {
            dispatch({
              type: LOGOUT
            });
          }
        } catch (err) {
          dispatch({
            type: LOGOUT
          });
        }
      };

      init();
    }, []);*/

  /*const login = async (email: string, password: string) => {
    const result = await service.login(email, password);
    const userP = await service.getUserInfo(result.token);

    return result;
    /!*await auth0Client.loginWithPopup({});
    const isLoggedIn = await auth0Client.isAuthenticated();

    if (isLoggedIn) {
      const user = await auth0Client.getUser();
      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user: {
            id: user?.sub,
            avatar: user?.picture,
            email: user?.email,
            name: user?.name,
            tier: 'Premium'
          }
        }
      });
    }*!/
  };*/

  /*const logout = async () => {
    await service.logout();

    dispatch({
      type: LOGOUT
    });
  };*/

  /*const resetPassword = async (email: string) => {};

  const updateProfile = () => {};*/

  // Memoised so consumers only get a new value when auth state actually changes,
  // not on every render of the provider.
  const value = useMemo<Auth0ContextType>(
    () => ({
      ...currentState,
      refreshToken,
      user: user,
      getAccessToken: () => Promise.resolve(currentState.accessToken),
      logout: (returnTo?: string) => {
        // Drop the PostHog identity so the next login on this browser
        // doesn't inherit this user's events/flags.
        analytics.reset();
        return (service.logout as any)(returnTo);
      }
    }),
    [currentState, refreshToken, user]
  );

  if (currentState.isInitialized !== undefined && !currentState.isInitialized) {
    return <Loader />;
  }

  return <Auth0Context.Provider value={value}>{children}</Auth0Context.Provider>;
};

export default Auth0Context;
